import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import Lottie from 'react-lottie';

import {
  fetchPreviousOfflineConsultation,
  getAllRelatives,
  getAllDopeRelatives,
  fetchConsultationBlocker,
  refundStatusOffline,
} from '../../services/consultation';
import clientConfig from '../../clientConfig';
import { logEvent } from '../../utils/logEvent';
import Header from '../../components/common/Header';
import initializeFreshDesk from '../../utils/freshchat';
import {
  resetChat,
  setCurrentActiveTab,
  getPreBookedProcesses,
} from '../../actions';
import { FadeInTransition } from '../../components/transitions/transitions';
import ConsultationUser from '../../components/consultations/ConsultationUser';
import RefundStatusTele from '../../components/consultations/new-flow/RefundStatusTele';
import LoadingSkeleton from '../../components/common/Skeleton/ConsultationStartPreview';
import AppointmentCard from '../../components/cards/AppointmentCard';

import noConsultationLottie from '../../lottie/no-consultations.json';
import OrImage from '../../images/pre-book/Or.svg';
import FooterButton from '../../components/common/FooterButton';
import PreBookInfoModal from '../../components/consultations/PreBookInfoModal';
import PatientModal from '../../components/common/PatientModal';
import { SERVICE_LINE_NAMES } from '../../config';
import dismissNativeWebViewOrNavigateHome from '../../utils/dismissNativeWebViewOrNavigateHome';

const OuterContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: stretch;
  background: white;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  height: 100vh;
`;

const BodyContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-self: stretch;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 10rem;
  gap: 10px;
  height: 100vh;
`;

const PreBookDetails = styled.div`
  margin-top: -5px;
`;

const BodyInnerContainer = styled.div`
  // padding-left: 14px;
  // padding-right: 14px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ConsultationStartPreview = ({ history, location }) => {
  const dispatch = useDispatch();
  const authToken = useSelector((state) => state.user.authToken);
  const user = useSelector((state) => state.user);
  const magicBasedLogin = useSelector((state) => state.app.magicBasedLogin);

  const { state: locationState } = location;

  const [ongoingAppointment, setOngoingAppointment] = useState([]);
  const [completedAppointment, setCompletedAppointment] = useState([]);
  const [relatives, setRelatives] = useState([]);
  const [selectedRelative, setSelectedRelative] = useState(null);
  const [showRelativeModal, setShowRelativeModal] = useState(false);
  const [blockers, setBlockers] = useState({});
  const [refundStatusModal, setRefundStatusModal] = useState(false);
  const [refundData, setRefundData] = useState({});
  const [refundLoading, setRefundLoading] = useState(true);
  const [preBookCardDetails, setPreBookCardDetails] = useState([]);
  const [offlineCardDetails, setOfflineCardDetails] = useState([]);
  const [showPreBookInfoModal, setShowPreBookInfoModal] = useState(false);
  const [selectedPreBookCard, setSelectedPreBookCard] = useState(null);
  const [preBookedData, setPreBookedData] = useState(null);
  const [loading, setLoading] = useState(false);

  const openConsultIdRef = useRef(
    locationState?.openConsultId ? parseInt(locationState.openConsultId) : null
  );

  const openOrder = (order) => {
    if (order.type === 'reimbursement') {
      history.push('/consultation/reimbursement-details', {
        reimbursementId: order.reimbursementId,
        goBack: true,
      });
      return;
    }
    if (order.prebookStatus !== 'cl-generated' && order.prebookedAppointment) {
      logEvent('Self book Appointment created');
      history.push('/consultation/prebook-status', {
        requestId: order.requestId,
      });
      return;
    }
    history.push('/consultation/appointment/appointment-status', {
      requestId: order.requestId,
      blockers,
    });
  };

  const getConsults = (id = null) => {
    setLoading(true);
    const relativeId = id || selectedRelative?.id || 'all';
    fetchPreviousOfflineConsultation(relativeId, authToken)
      .then((res) => {
        const cards = res.cards;
        console.log('cards fetchPreviousOfflineConsultation', cards);

        const ongoing = res?.appointments?.filter(
          (appt) => appt.status.ongoing === true
        );
        const completed = res?.appointments?.filter(
          (appt) => appt.status.ongoing === false
        );

        if (openConsultIdRef.current && res?.appointments?.length > 0) {
          const orderToOpen = res.appointments.filter(
            (ele) => ele.consultationId === openConsultIdRef.current
          );
          if (orderToOpen.length === 1) {
            openOrder(orderToOpen[0]);
          }
        }

        setOngoingAppointment(ongoing);
        setCompletedAppointment(completed);
        setPreBookCardDetails(
          cards?.filter((item) => item.label === 'You Book We Pay')
        );
        setOfflineCardDetails(
          cards?.filter((item) => item.label === 'Book Appointment Via Visit')
        );
      })
      .catch((err) => {
        console.error('Error in fetching consultation', err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchRelatives = () => {
    const fetchFn = clientConfig.useDopeRelatives
      ? getAllDopeRelatives
      : getAllRelatives;
    fetchFn(authToken)
      .then((res) => {
        const self = {
          age: user.userAge,
          dob: user.dateOfBirth,
          gender: user.userGender,
          id: 'self',
          name: user.userName,
          phone: user.userPhone,
          relation: user.userType,
        };
        setRelatives([self, ...res.relatives]);
        if (clientConfig.useDopeRelatives) {
          const relativeId =
            res.relatives?.[0]?.relativeId ?? res.relatives?.[0]?.id;
          if (relativeId && relativeId !== -1) {
            sessionStorage.setItem('consultRelativeId', relativeId);
          } else {
            sessionStorage.removeItem('consultRelativeId');
          }
        }
      })
      .catch((err) => {
        console.error('Error in fetching relatives', err);
      });
  };

  const fetchBlocker = () => {
    fetchConsultationBlocker(authToken)
      .then((res) => {
        setBlockers(res.blockers);
      })
      .catch((err) => {
        console.error('Error in fetching consultation blocker', err);
      });
  };

  const refundStatus = (requestId) => {
    setRefundLoading(true);
    refundStatusOffline(requestId, authToken)
      .then((res) => {
        setRefundData(res?.refundDetails);
        setRefundLoading(false);
      })
      .catch((err) => {
        console.error('Error in refundStatusOffline', err);
      });
  };

  const selectRelative = (ele) => {
    setSelectedRelative(ele);
    getConsults(ele?.id);
    setShowRelativeModal(false);
  };

  const closeView = () => {
    dismissNativeWebViewOrNavigateHome(history, location);
  };

  useEffect(() => {
    window.gtag('event', 'screen_view', { screen_name: 'Consult Screen' });
    dispatch(resetChat());
    getConsults('all');
    fetchRelatives();
    fetchBlocker();
    logEvent('Offline Appointments Home Screen');

    if (locationState?.from === 'home') {
      dispatch(setCurrentActiveTab('home'));
    } else {
      dispatch(setCurrentActiveTab('clinic-orders'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const keyOption = {
    loop: true,
    autoplay: true,
    animationData: noConsultationLottie,
    rendererSettings: { preserveAspectRatio: 'xMidYMid slice' },
  };

  return (
    <FadeInTransition in={true} timeout={200} mountOnEnter unmountOnExit appear>
      <OuterContainer>
        <Header title={'Clinic Consultations'} onTap={closeView}>
          {relatives?.length > 1 && (
            <ConsultationUser
              text={selectedRelative ? selectedRelative.relation : 'All'}
              onTap={() => setShowRelativeModal(true)}
            />
          )}
        </Header>
        <BodyContainer>
          <div>
            {offlineCardDetails &&
              offlineCardDetails.length > 0 &&
              offlineCardDetails?.map((card) => {
                return (
                  <div
                    key={card.bannerImage}
                    onClick={() => {
                      if (blockers.cancelledRequest > 0) {
                        history.push('/consultation/cancel-blocker', {
                          requestId: blockers.cancelledRequest,
                        });
                      } else if (blockers.pendingRequestId > 0) {
                        history.push(
                          '/consultation/appointment/appointment-status',
                          {
                            requestId: blockers.pendingRequestId,
                            goBack: true,
                            blockers: blockers,
                          }
                        );
                      } else {
                        logEvent('Book Appointment Via Visit clicked');
                        history.push('/consultation/start', {
                          showVertical: card.showVerticals,
                        });
                      }
                    }}
                  >
                    <img src={card.bannerImage} alt="Offline Card" />
                  </div>
                );
              })}
          </div>

          {preBookCardDetails &&
            preBookCardDetails.length > 0 &&
            offlineCardDetails &&
            offlineCardDetails.length > 0 &&
            !loading && <img src={OrImage} alt="OrImage" />}

          <BodyInnerContainer>
            <PreBookDetails>
              {preBookCardDetails &&
                preBookCardDetails.length > 0 &&
                preBookCardDetails?.map((card) => {
                  return (
                    <div
                      key={card.bannerImage}
                      onClick={() => {
                        logEvent(
                          'Funnel Offline Consultation Self Book Card Clicked'
                        );
                        dispatch(
                          getPreBookedProcesses((data) => {
                            setShowPreBookInfoModal(true);
                            setSelectedPreBookCard(card);
                            setPreBookedData(data);
                          })
                        );
                      }}
                    >
                      <img src={card.bannerImage} alt="Prebook Card" />
                    </div>
                  );
                })}
            </PreBookDetails>

            {ongoingAppointment.length > 0 && (
              <div className="px-14px">
                <div className="text-body-l font-semibold text-txtlight mb-20px ">
                  Ongoing
                </div>
                {ongoingAppointment.map((appt) => (
                  <AppointmentCard
                    {...appt}
                    key={appt.requestId}
                    patientName={appt.patientName}
                    requestId={appt.requestId}
                    bookingDate={appt.bookedOn}
                    doctorName={appt.newDoctorName || appt.doctorName}
                    prevDoctorName={appt.previousDoctorName}
                    fees={appt.newFees || appt.doctorFees}
                    prevFees={appt.previousFees}
                    appointmentDate={appt.newDate || appt.appDate}
                    prevAppointmentDate={appt.previousDate}
                    slot={appt.newSlot || appt.timeSlot}
                    prevSlot={appt.previousSlot}
                    pendingCopay={Number(appt.coPay || 0)}
                    status={appt.status}
                    onTap={() => openOrder(appt)}
                    cancelCharge={appt.cancelAppointmentCharge}
                    modalText={appt.noShowText}
                    onReload={(id) => getConsults(id)}
                    letter={appt.cashlessLetterLink}
                    tooltipText={appt.status.toolTip}
                  />
                ))}
              </div>
            )}

            {completedAppointment.length > 0 && !loading && (
              <div className="px-14px">
                <div className="text-body-l font-semibold text-txtlight mb-8 mt-8">
                  Completed
                </div>
                {completedAppointment.map((appt) => (
                  <AppointmentCard
                    {...appt}
                    key={appt.requestId}
                    patientName={appt.patientName}
                    requestId={appt.requestId}
                    bookingDate={appt.bookedOn}
                    doctorName={appt.newDoctorName || appt.doctorName}
                    prevDoctorName={appt.previousDoctorName}
                    fees={appt.newFees || appt.doctorFees}
                    prevFees={appt.previousFees}
                    appointmentDate={appt.newDate || appt.appDate}
                    prevAppointmentDate={appt.previousDate}
                    slot={appt.newSlot || appt.timeSlot}
                    prevSlot={appt.previousSlot}
                    pendingCopay={Number(appt.coPay || 0)}
                    status={appt.status}
                    onTap={() => openOrder(appt)}
                    letter={appt.cashlessLetterLink}
                    cancelCharge={appt.cancelAppointmentCharge}
                    modalText={appt.noShowText}
                    onReload={(id) => getConsults(id)}
                    tooltipText={appt.status.toolTip}
                    setRefundStatusModal={setRefundStatusModal}
                    refundStatus={refundStatus}
                  />
                ))}
              </div>
            )}

            <div className="px-14px h-full">
              {loading ? (
                <div style={{ height: '100svh', background: '#ffffff' }}>
                  <LoadingSkeleton />
                </div>
              ) : (
                ongoingAppointment?.length === 0 &&
                completedAppointment?.length === 0 && (
                  <div className="flex flex-col justify-center items-center w-full h-full pb-20px">
                    <Lottie options={keyOption} width={'60%'} />
                    <p className="text-title-l text-primary-button font-bold text-center">
                      Nothing to see here!
                    </p>
                    <FooterButton
                      onClick={() => history.push('/consultation/start')}
                      buttonText={'Book Appointment'}
                    />
                  </div>
                )
              )}
            </div>
          </BodyInnerContainer>

          {showRelativeModal && (
            <PatientModal
              serviceLine={SERVICE_LINE_NAMES.OFFLINE}
              hideModal={() => setShowRelativeModal(false)}
              selectedPatient={selectedRelative}
              selectPatient={selectRelative}
              getPatient={
                clientConfig.useDopeRelatives
                  ? () => getAllDopeRelatives(authToken)
                  : undefined
              }
            />
          )}
        </BodyContainer>

        {refundStatusModal && (
          <RefundStatusTele
            closeModal={() => setRefundStatusModal(false)}
            refundData={{ ...refundData, type: 'offline' }}
            refundLoading={refundLoading}
          />
        )}

        {showPreBookInfoModal && (
          <PreBookInfoModal
            data={preBookedData}
            onClose={() => setShowPreBookInfoModal(false)}
            onComplete={() => {
              setShowPreBookInfoModal(false);
              history.push('/consultation/start', {
                showVertical: selectedPreBookCard?.showVerticals,
                card: 'pre-book-appointment',
              });
            }}
          />
        )}
      </OuterContainer>
    </FadeInTransition>
  );
};

export default ConsultationStartPreview;
